import React, { useState } from 'react';
import { useCountUp } from '../hooks/useCountUp';

const Sidebar = ({
  isOpen, onClose,
  ragEnabled, setRagEnabled, stats,
  conversations, activeChatId, setActiveChatId,
  newConversation, deleteConversation,
  theme, toggleTheme
}) => {
  // Animate theme-toggle icon on click
  const [toggleFlip, setToggleFlip] = useState(false);
  const handleToggle = () => {
    setToggleFlip(true);
    toggleTheme();
    setTimeout(() => setToggleFlip(false), 450);
  };

  // Animated stat counters
  const queriesDisplay = useCountUp(stats.queriesToday);
  const chatsDisplay   = useCountUp(conversations.length);

  // Format latency (not a plain number so we handle separately)
  const latencyDisplay = stats.avgLatencyMs != null
    ? `${(stats.avgLatencyMs / 1000).toFixed(1)}s`
    : '—';

  const getIcon = (title) => {
    const t = title.toLowerCase();
    if (t.includes('invoice') || t.includes('overdue') || t.includes('ar')) return 'fa-money-bill-wave';
    if (t.includes('stock') || t.includes('inventory') || t.includes('reorder')) return 'fa-boxes-stacked';
    if (t.includes('dunning') || t.includes('collection')) return 'fa-envelope-open-text';
    if (t.includes('credit') || t.includes('order') || t.includes('sales')) return 'fa-handshake';
    return 'fa-comment-dots';
  };

  return (
    <>
      {/* ── Sidebar panel ──
          Mobile:  fixed, off-screen by default, slides in when isOpen
          Desktop (md+): fixed, always visible
      ── */}
      <div
        className={`
          fixed top-0 left-0 h-full w-72 z-40
          glass flex flex-col border-r border-white/60
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* ── Logo + theme toggle + mobile close button ── */}
        <div className="flex items-center gap-3 p-5 pb-4">
          {/* Theme-aware Averina AI logo */}
          <div className="logo-breathe w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-white">
            <img
              src={theme === 'dark' ? '/averina-ai-app-icon.png' : '/averina-ai-icon.png'}
              alt="Averina AI"
              className="w-full h-full object-contain transition-opacity duration-300"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-gray-800 text-sm truncate">SAP B1 Agent</h1>
            <p className="text-[11px] text-indigo-400 font-medium">Phi-3 · RAG · Agentic AI</p>
          </div>

          {/* Theme toggle */}
          <button
            onClick={handleToggle}
            title={theme === 'dark' ? 'Switch to Light mode' : 'Switch to Dark mode'}
            className={`w-8 h-8 flex items-center justify-center rounded-xl transition-all duration-200 hover:scale-110 flex-shrink-0 ${toggleFlip ? 'theme-flip' : ''}`}
            style={{
              background: theme === 'dark' ? 'rgba(79,70,229,0.20)' : 'rgba(219,234,254,1)',
              color:      theme === 'dark' ? '#fde68a'              : '#1e40af',
            }}
          >
            <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} text-sm`}></i>
          </button>

          {/* Close button — mobile only */}
          <button
            onClick={onClose}
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200 flex-shrink-0"
            aria-label="Close sidebar"
          >
            <i className="fas fa-xmark text-sm"></i>
          </button>
        </div>

        {/* ── New conversation (shimmer button) ── */}
        <div className="px-4 mb-4">
          <button
            onClick={() => { newConversation(); onClose(); }}
            className="btn-shimmer w-full flex items-center justify-center gap-2 text-white py-2.5 rounded-xl text-sm font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-100 transition-all duration-200"
          >
            <i className="fas fa-plus text-xs"></i> New conversation
          </button>
        </div>

        {/* ── Conversations ── */}
        <div className="px-3 mb-1">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest px-1">Conversations</p>
        </div>

        <div className="flex-1 overflow-y-auto px-3 space-y-0.5 pb-2">
          {conversations.map((conv, idx) => {
            const isActive  = conv.id === activeChatId;
            const msgCount  = conv.messages.filter(m => m.role === 'user').length;
            return (
              <div
                key={conv.id}
                onClick={() => { setActiveChatId(conv.id); onClose(); }}
                className={`conv-item group flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all duration-200 ${
                  isActive ? 'conv-active' : 'hover:bg-white/60'
                }`}
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs flex-shrink-0 transition-all duration-200 ${
                  isActive
                    ? 'bg-indigo-100 text-indigo-600 scale-110'
                    : 'bg-gray-100 text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-500 group-hover:scale-110'
                }`}>
                  <i className={`fas ${getIcon(conv.title)}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs truncate font-medium ${isActive ? 'text-indigo-700' : 'text-gray-600'}`}>
                    {conv.title}
                  </p>
                  {msgCount > 0 && (
                    <p className="text-[10px] text-gray-400">{msgCount} message{msgCount !== 1 ? 's' : ''}</p>
                  )}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); deleteConversation(conv.id); }}
                  className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-300 transition-all flex-shrink-0 hover:scale-110"
                  title="Delete"
                >
                  <i className="fas fa-trash text-[10px]"></i>
                </button>
              </div>
            );
          })}
        </div>

        {/* ── RAG + Stats ── */}
        <div className="p-4 border-t border-white/50">
          {/* RAG toggle */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs transition-colors duration-300 ${ragEnabled ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                <i className="fas fa-book-open"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700">RAG Enhancement</p>
                <p className="text-[10px] text-gray-400">SAP B1 knowledge base</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={ragEnabled} onChange={e => setRagEnabled(e.target.checked)} />
              <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4 shadow-inner"></div>
            </label>
          </div>

          {/* RAG status with animated ping ring */}
          <div className={`flex items-center gap-2 mb-4 text-[10px] font-medium transition-colors duration-300 ${ragEnabled ? 'text-emerald-600' : 'text-gray-400'}`}>
            {ragEnabled ? (
              <>
                <span className="rag-ping"><span className="rag-dot"></span></span>
                Active — cites SAP B1 docs
              </>
            ) : (
              <>
                <i className="fas fa-circle-minus"></i>
                Off — base model only
              </>
            )}
          </div>

          {/* Stats — animated counting numbers */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: queriesDisplay, label: 'Today' },
              { value: latencyDisplay,  label: 'Avg latency' },
              { value: chatsDisplay,   label: 'Chats' },
            ].map(({ value, label }) => (
              <div
                key={label}
                className="stat-card rounded-xl p-2 text-center hover:scale-105 transition-transform duration-200"
              >
                <p className="font-bold text-sm text-indigo-700 tabular-nums">{value}</p>
                <p className="text-[9px] text-gray-400 font-medium uppercase tracking-wide mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
